import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { AccountingApiService } from '@app/modules/businesses/services/accounting.api';
import { MyBusinessApiService } from '@app/modules/my-business/services/my-business.api.service';
import { BehaviorSubject, combineLatest, map } from 'rxjs';
import { Connection as ApideckConnection, ApideckVault } from '@apideck/vault-js';
import { Connection } from '@app/modules/businesses/models/accounting.interface';
import { PusherService } from '@app/shared/services/pusher/pusher.service';

@Injectable({
  providedIn: 'root',
})
export class AccountingService {
  // Dependencies
  private readonly myBusinessApiService = inject(MyBusinessApiService);
  private readonly accountingApiService = inject(AccountingApiService);
  private readonly pusher = inject(PusherService);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  // State
  private readonly initialized$ = new BehaviorSubject<boolean>(false);
  public connectionId$ = new BehaviorSubject<number>(null);
  private readonly businessUuid$ = new BehaviorSubject<string>(null);
  public business$ = new BehaviorSubject(null);
  public accountOptions$ = new BehaviorSubject(null);
  public trackingCategoryOptions$ = new BehaviorSubject(null);
  public connecting$ = new BehaviorSubject<boolean>(false);
  public loadingVault$ = new BehaviorSubject<boolean>(false);
  public connections$ = new BehaviorSubject<Connection[]>([]);
  public connection$ = new BehaviorSubject<Connection>(null);
  public failedOnboarding$ = new BehaviorSubject<boolean>(false);
  public finishedOnboarding$ = new BehaviorSubject<boolean>(false);
  public accountingSoftwareSynced$ = new BehaviorSubject<Connection>(null);
  public config$ = new BehaviorSubject<{
    accounts: any[];
    properties: any[];
  }>(null);
  constructor() {
    this.loadConnections();
    this.listenForPusherEvents();
  }

  public formState$ = new BehaviorSubject<{
    data: any;
    loading: boolean;
    error: string | null;
  }>({
    data: null,
    loading: false,
    error: null,
  });

  public get businessUuid() {
    return this.businessUuid.value;
  }

  public openVault(businessUuid: string) {
    this.loadingVault$.next(true);
    this.accountingApiService.createBusinessAccountingConnection(businessUuid).subscribe((res) => {
      ApideckVault.open({
        token: res.token,
        serviceId: 'quickbooks',
        showAttribution: false,
        unifiedApi: 'accounting',
        onReady: () => {
          this.loadingVault$.next(false);
        },
        onConnectionChange: (connection: ApideckConnection) => {
          if (connection.state !== 'callable') {
            return;
          }

          this.connecting$.next(true);
          ApideckVault.close();
        },
      });
    });
  }

  public init(businessUuid: string, connectionId: number) {
    if (this.initialized$.value) {
      return;
    }

    this.formState$.next({
      loading: true,
      error: null,
      data: null,
    });

    this.initialized$.next(true);
    this.businessUuid$.next(businessUuid);
    this.connectionId$.next(Number(connectionId));

    this.connections$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((connections) => {
      this.connection$.next(connections.find((c) => c.id === Number(connectionId)));
    });

    combineLatest([
      this.myBusinessApiService.getBusiness(businessUuid),
      this.accountingApiService.getAccountOptions(businessUuid),
      this.accountingApiService.getAccountingConnectionConfig(connectionId),
      this.accountingApiService.getTrackingCategories(connectionId),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([business, accountOptions, config, trackingCategories]) => {
        this.business$.next(business);
        this.accountOptions$.next(accountOptions);
        this.trackingCategoryOptions$.next(trackingCategories);
        this.config$.next(config);

        this.formState$.next({
          loading: false,
          error: null,
          data: {
            properties: {
              fieldMap: business.properties.reduce((acc, property) => {
                const configuredProperty = config.properties.find((p) => p.id === property.id);
                acc[property.id] = {
                  label: property.name,
                  value: configuredProperty?.account_id ?? '',
                  isDefault: Boolean(configuredProperty?.tracking_category?.is_default),
                };
                return acc;
              }, {}),
              options: trackingCategories?.map(({ id, name }) => ({
                value: id,
                label: name,
              })),
            },
            accounts: {},
            sync: {},
          },
        });
      });
  }

  private loadConnections() {
    this.accountingApiService.getAccountingConnections().subscribe((connections) => {
      this.connections$.next(connections);
    });
  }

  private listenForPusherEvents() {
    this.pusher.accountingConnectionUpdate$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      if (data.type === 'failed_onboarding') {
        this.failedOnboarding$.next(true);
        return;
      }

      if (data.type === 'finished_onboarding') {
        this.finishedOnboarding$.next(true);
        return;
      }

      if (data.connection.state === 'callable' && data.connection.id) {
        this.accountingSoftwareSynced$.next(data.connection);
      }
    });
  }
}
